import '@tanstack/react-router';

export type IconName = 'reports' | 'published' | 'agents' | 'settings';

/** Metadata a route declares in staticData. A workspace page appears in navigation only through this. */
export interface NavMeta {
  label: string;
  icon: IconName;
  placement: 'main' | 'bottom';
  order: number;
}

export interface SettingsMeta {
  label: string;
  order: number;
}

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    crumb?: string;
    rail?: boolean;
    nav?: NavMeta; // a sidebar/rail section; the route is the section's link target
    settingsSection?: SettingsMeta; // one Settings page
    navExempt?: string; // why a parameter-free page has no navigation entry
  }
}

export interface NavSection extends NavMeta {
  id: string;
  to: string; // to = the route's fullPath
}

export interface SettingsSection extends SettingsMeta {
  id: string;
  to: string;
}

/** The minimal route shape the derivations read; the values of a TanStack router's routesById satisfy it (probed on 1.170.41; there is no flatRoutes). */
export interface RouteLike {
  id: string;
  fullPath: string;
  options: { staticData?: { nav?: NavMeta; settingsSection?: SettingsMeta } };
}

export function navSectionsFrom(routes: Iterable<RouteLike>): NavSection[] {
  const sections: NavSection[] = [];
  for (const route of routes) {
    const nav = route.options.staticData?.nav;
    if (nav) {
      sections.push({
        ...nav,
        id: route.id,
        to: route.fullPath,
      });
    }
  }
  // Sort by order
  sections.sort((a, b) => a.order - b.order);
  return sections;
}

export function settingsSectionsFrom(routes: Iterable<RouteLike>): SettingsSection[] {
  const sections: SettingsSection[] = [];
  for (const route of routes) {
    const settingsSection = route.options.staticData?.settingsSection;
    if (settingsSection) {
      sections.push({
        ...settingsSection,
        id: route.id,
        to: route.fullPath,
      });
    }
  }
  // Sort by order
  sections.sort((a, b) => a.order - b.order);
  return sections;
}

/** Substitutes the workspace id into a fullPath such as /w/$workspaceId/reports. */
export function sectionPath(section: { to: string }, workspaceId: string): string {
  return section.to.replace('$workspaceId', encodeURIComponent(workspaceId));
}
